import { createFileRoute, Outlet, useLocation } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { fetchCustomerByIdQueryOptions } from "@/api/customers";
import { PageHeader } from "@/components/PageHeader";
import { useApiParameters } from "@/hooks/useApiParameters";
import { CustomerDetailsNavigation } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/CustomerDetailsNavigation";
import { DeleteCustomerButton } from "@/routes/_auth/app/_app/org/$orgId/customers/-components/DeleteCustomerButton";

export const Route = createFileRoute(
	"/_auth/app/_app/org/$orgId/customers/$customerId",
)({
	loader: async ({ context, params }) => {
		const { userId, token } = useApiParameters(
			context.userId,
			await context.get_token(),
		);
		const { orgId, customerId } = params;

		// Preload customer data
		context.queryClient.ensureQueryData(
			fetchCustomerByIdQueryOptions(userId, token, orgId, customerId),
		);

		return { userId, token, orgId, customerId };
	},
	component: CustomerDetailsLayout,
});

function CustomerDetailsLayout() {
	const { t } = useTranslation();
	const { userId, token, orgId, customerId } = Route.useLoaderData();
	const location = useLocation();

	// Determine active tab based on current route
	const getActiveTab = () => {
		const pathname = location.pathname;
		if (pathname.endsWith("/edit")) return "edit";
		if (pathname.includes("/enrollments")) return "enrollments";
		if (pathname.includes("/medicalRecords")) return "medicalRecords";
		if (pathname.includes("/sessions")) return "sessions";
		if (pathname.includes("/billing")) return "billing";
		return "overview"; // default to overview
	};

	return (
		<div className="flex flex-col h-full">
			<PageHeader
				title={t("customers.navigation.title")}
				description={t("customers.navigation.description")}
				showBackButton={true}
				backButtonText={t("common.back")}
				hideOnMobile={false}
				actions={
					<DeleteCustomerButton
						organizationId={orgId}
						customerId={customerId}
						userId={userId}
						token={token}
						className="sm:hidden"
						testId="customer-header-delete-button"
					/>
				}
			/>

			{/* Main content area with side navigation */}
			<div
				className="flex-1 flex flex-col md:flex-row h-[calc(100vh-4rem)] min-h-0 overflow-hidden"
				data-testid="customer-details-container"
			>
				{/* Side Navigation */}
				<CustomerDetailsNavigation
					activeTab={getActiveTab()}
					userId={userId}
					token={token}
				/>

				{/* Main Content Area */}
				<div
					className="flex-1 p-3 md:p-4 bg-base-200 min-h-0 overflow-auto"
					data-testid="customer-details-main-content-area"
				>
					<Outlet />
				</div>
			</div>
		</div>
	);
}
